import { alert } from 'actions/flash'
import ConfirmModal from 'components/ConfirmModal'
import ColorPicker from 'components/Form/ColorPicker'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import Section from 'components/Section'
import Formsy from 'formsy-react'
import { useAlertBannerForm } from 'hooks/useAlertBannerForm'
import useDispatch from 'hooks/useDispatch'
import {
  formatPermissionReason,
  formatPermissionReasonOr,
  usePermissionLabel,
  useRbacManageDisabled,
  useRbacNavDisabled,
} from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import { AlertBanner as AlertBannerModel } from 'models/Banner'
import React from 'react'
import { I18n } from 'react-redux-i18n'
import { Link } from 'react-router-dom'
import CmsPage from 'scenes/Cms/components/CmsPage'
import PublishBanner from 'scenes/Cms/components/PublishBanner'
import Sidebar from './Sidebar'

const t = buildTranslate('cms.content.alert_banner')

type FormModel = {
  backgroundColor: string
  textColor: string
}

const mapFromBanner = (banner: AlertBannerModel): FormModel => ({
  backgroundColor: banner.background_color,
  textColor: banner.text_color,
})

const mapToPayload = (
  form: FormModel,
  _banner: AlertBannerModel
): Partial<AlertBannerModel> => ({
  background_color: form.backgroundColor,
  text_color: form.textColor,
})

const AlertBannerStyling = () => {
  const dispatch = useDispatch()

  const {
    formModel,
    setFormModel,
    isLoading,
    loadError,
    isSubmitting,
    isModalOpen,
    setIsModalOpen,
    publishChanges,
  } = useAlertBannerForm({ mapFromBanner, mapToPayload })

  // The Settings page is gated on OrderingManagement OR CMS (matches the OC
  // nav link). Mirror that OR here so the tooltip reflects the real gate.
  const orderingMgmtDisabled = useRbacNavDisabled('OrderingManagement')
  const cmsDisabled = useRbacNavDisabled('CMS')
  const settingsDisabled = orderingMgmtDisabled && cmsDisabled
  const orderingMgmtLabel = usePermissionLabel('OrderingManagement')
  const cmsLabel = usePermissionLabel('CMS')
  const settingsReason = formatPermissionReasonOr(
    [orderingMgmtLabel, cmsLabel],
    settingsDisabled
  )

  // Narrower than the Settings page: the server gates styling fields on
  // rbac_authorize!('manage', 'content_management') alone, not the OR.
  const stylingDisabled = useRbacManageDisabled('CMS')
  const stylingReason = formatPermissionReason(cmsLabel, stylingDisabled)
  // Default the trailing fragment to '' so an omitted {{link}} marker in a
  // future translation degrades gracefully (link renders at the end of the
  // sentence rather than producing `undefined` in the JSX).
  const [descriptionBefore, descriptionAfter = ''] = t(
    'styling_description'
  ).split('{{link}}')
  const description = (
    <>
      {descriptionBefore}
      <PermissionTooltip
        disabled={settingsDisabled}
        reason={settingsReason}
        id="alert-banner-styling-settings-link"
        placement="top"
        inline
      >
        {settingsDisabled ? (
          <span className="grey-50">{t('styling_description_link')}</span>
        ) : (
          <Link to="/alert_banners">{t('styling_description_link')}</Link>
        )}
      </PermissionTooltip>
      {descriptionAfter}
    </>
  )

  // GET failed — render the shell with an error message. Otherwise the
  // `isLoading` branch below would spin forever because `formModel`
  // never materialises.
  if (loadError) {
    return (
      <CmsPage
        title={t('styling_title')}
        description={description}
        badges={[t('app_web')]}
        sidebar={<Sidebar />}
      >
        <p className="mt-l body-text-3 grey-70" role="alert">
          {t('load_error')}
        </p>
      </CmsPage>
    )
  }

  if (!formModel) {
    return (
      <CmsPage
        title={t('styling_title')}
        description={description}
        badges={[t('app_web')]}
        sidebar={<Sidebar />}
        isLoading
      >
        <></>
      </CmsPage>
    )
  }

  return (
    <Formsy
      onValidSubmit={() => {
        setIsModalOpen(true)
      }}
      onChange={newFormModel => {
        setFormModel(newFormModel)
      }}
      onInvalidSubmit={() =>
        dispatch(
          alert({
            key: 'danger',
            message: I18n.t('cms.content.form_error'),
            timeout: 5,
          })
        )
      }
    >
      <CmsPage
        title={t('styling_title')}
        description={description}
        badges={[t('app_web')]}
        sidebar={<Sidebar />}
        isLoading={isLoading}
      >
        <Section className="p-m mb-m">
          <ColorPicker
            name="backgroundColor"
            label={t('banner_color')}
            validations={{
              hex: true,
              isRequired: true,
            }}
            value={formModel.backgroundColor}
            validationErrors={{
              hex: I18n.t('validations.hex.message'),
              isRequired: I18n.t('validations.is_required'),
            }}
            disabled={stylingDisabled}
            startEnhancer
          />
          <p className="body-text-5 grey-70 mt-xs mb-m">
            {t('color_description')}
          </p>
          <ColorPicker
            name="textColor"
            label={t('text_color')}
            validations={{
              hex: true,
              isRequired: true,
            }}
            value={formModel.textColor}
            validationErrors={{
              hex: I18n.t('validations.hex.message'),
              isRequired: I18n.t('validations.is_required'),
            }}
            disabled={stylingDisabled}
            startEnhancer
          />
        </Section>
        <PermissionTooltip
          disabled={stylingDisabled}
          reason={stylingReason}
          id="alert-banner-styling-publish"
        >
          <PublishBanner
            buttonText={t('publish_styling')}
            disabled={stylingDisabled}
          />
        </PermissionTooltip>
      </CmsPage>
      <ConfirmModal
        title={t('modal.publish_changes')}
        description={t('modal.you_are_publishing_styling')}
        confirmText={t('modal.publish')}
        isOpen={isModalOpen}
        isLoading={isSubmitting}
        onClose={() => setIsModalOpen(false)}
        onConfirm={() => publishChanges(formModel)}
      />
    </Formsy>
  )
}

export default AlertBannerStyling
